/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { isBazaarvoiceEnabled } from './constants';
import type { BazaarvoiceConfig, BazaarvoiceProductLike } from './types';

function readCustomValue(product: BazaarvoiceProductLike | undefined, keys: string[]): unknown {
    if (!product) {
        return undefined;
    }
    for (const key of keys) {
        if (product[key] !== undefined && product[key] !== null && product[key] !== '') {
            return product[key];
        }
    }
    const attrs = product.customAttributes;
    if (Array.isArray(attrs)) {
        for (const key of keys) {
            const match = attrs.find((attr) => attr.id === key || attr.id === key.replace(/^c_/, ''));
            if (match?.value !== undefined && match.value !== null && match.value !== '') {
                return match.value;
            }
        }
    }
    const represented = product.representedProduct;
    if (represented && typeof represented === 'object') {
        return readCustomValue(represented as BazaarvoiceProductLike, keys);
    }
    return undefined;
}

export function replaceIllegalCharacters(rawId: string): string {
    return rawId
        .replace(/&/g, '_and_')
        .replace(/\//g, '_fslash_')
        .replace(/Master-/g, '');
}

export function getRawCatalogId(product: BazaarvoiceProductLike | undefined): string {
    if (!product) {
        return '';
    }
    const masterId = product.master?.masterId;
    if (typeof masterId === 'string' && masterId) {
        return masterId;
    }
    if (typeof product.id === 'string' && product.id) {
        return product.id;
    }
    if (typeof product.productId === 'string' && product.productId) {
        return product.productId;
    }
    return '';
}

export function getBazaarvoiceProductId(product: BazaarvoiceProductLike | undefined): string {
    return replaceIllegalCharacters(getRawCatalogId(product));
}

export function getProductBrand(product: BazaarvoiceProductLike | undefined): string {
    const brand = product?.brand ?? readCustomValue(product, ['c_brand', 'brand']);
    if (typeof brand === 'string') {
        return brand;
    }
    if (brand && typeof brand === 'object' && 'name' in brand && typeof brand.name === 'string') {
        return brand.name;
    }
    return '';
}

export function isBazaarvoiceDisplayEnabled(
    config: BazaarvoiceConfig | undefined,
    product?: BazaarvoiceProductLike
): boolean {
    if (!isBazaarvoiceEnabled(config)) {
        return false;
    }
    const allowlist = config.brandAllowlist ?? [];
    if (allowlist.length === 0) {
        return true;
    }
    if (!product) {
        return true;
    }
    const brand = getProductBrand(product).toLowerCase();
    return allowlist.some((entry) => entry.toLowerCase() === brand);
}

function toFiniteNumber(value: unknown): number | undefined {
    const num = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
    return Number.isFinite(num) ? num : undefined;
}

export function getBazaarvoiceAggregateRating(product: BazaarvoiceProductLike | undefined): {
    rating: number;
    reviewCount: number;
} | null {
    const rating = toFiniteNumber(
        readCustomValue(product, ['c_bvStarRating', 'bvStarRating', 'c_bvAverageRating', 'bvAverageRating'])
    );
    const reviewCount = toFiniteNumber(
        readCustomValue(product, ['c_bvTotalReviewCount', 'bvTotalReviewCount', 'c_bvReviewCount', 'bvReviewCount'])
    );
    if (rating === undefined || reviewCount === undefined || rating <= 0 || reviewCount <= 0) {
        return null;
    }
    return { rating, reviewCount };
}
